/**
 * RBAC — Role-Based Access Control
 * Pure logic, no DB dependency. Used by UI + Edge Functions.
 */

export type Role = "owner" | "admin" | "operator" | "viewer" | "superadmin";

export type Permission =
  | "org.read"
  | "org.update"
  | "org.delete"
  | "org.billing"
  | "members.read"
  | "members.invite"
  | "members.remove"
  | "members.change_role"
  | "groups.read"
  | "groups.create"
  | "groups.update"
  | "groups.delete"
  | "agents.read"
  | "agents.create"
  | "agents.update"
  | "agents.delete"
  | "skills.read"
  | "skills.create"
  | "skills.update"
  | "skills.delete"
  | "commands.read"
  | "commands.execute"
  | "prompts.read"
  | "prompts.update"
  | "sources.read"
  | "sources.manage"
  | "tickets.read"
  | "tickets.create"
  | "tickets.manage"
  | "sentinel.read"
  | "sentinel.manage"
  | "cortex.tom.read"
  | "cortex.dmn.read"
  | "cortex.dmn.manage"
  | "cortex.entities.read"
  | "cortex.entities.manage"
  | "admin.panel"
  | "admin.impersonate";

const ALL_PERMISSIONS: Permission[] = [
  "org.read", "org.update", "org.delete", "org.billing",
  "members.read", "members.invite", "members.remove", "members.change_role",
  "groups.read", "groups.create", "groups.update", "groups.delete",
  "agents.read", "agents.create", "agents.update", "agents.delete",
  "skills.read", "skills.create", "skills.update", "skills.delete",
  "commands.read", "commands.execute",
  "prompts.read", "prompts.update",
  "sources.read", "sources.manage",
  "tickets.read", "tickets.create", "tickets.manage",
  "sentinel.read", "sentinel.manage",
  "cortex.tom.read",
  "cortex.dmn.read", "cortex.dmn.manage",
  "cortex.entities.read", "cortex.entities.manage",
  "admin.panel", "admin.impersonate",
];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  superadmin: ALL_PERMISSIONS,
  owner: ALL_PERMISSIONS.filter((p) => p !== "admin.impersonate"),
  admin: ALL_PERMISSIONS.filter(
    (p) => p !== "admin.impersonate" && p !== "org.delete"
  ),
  operator: [
    "org.read",
    "members.read",
    "groups.read", "groups.create", "groups.update",
    "agents.read",
    "skills.read",
    "commands.read", "commands.execute",
    "prompts.read",
    "sources.read",
    "tickets.read", "tickets.create", "tickets.manage",
    "sentinel.read",
    "cortex.entities.read",
  ],
  viewer: [
    "org.read",
    "members.read",
    "groups.read",
    "agents.read",
    "skills.read",
    "commands.read",
    "prompts.read",
    "sources.read",
    "tickets.read",
    "sentinel.read",
    "cortex.entities.read",
  ],
} as const;

/**
 * Check if a role has a specific permission.
 */
export function hasPermission(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  const perms = ROLE_PERMISSIONS[role];
  if (!perms) return false;
  return perms.includes(permission);
}

/**
 * Get all permissions for a role.
 */
export function getPermissions(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role] || [];
}

/**
 * Check if role A is higher than role B in the hierarchy.
 */
const ROLE_HIERARCHY: Record<Role, number> = {
  superadmin: 100,
  owner: 80,
  admin: 60,
  operator: 40,
  viewer: 20,
};

export function isHigherRole(a: Role, b: Role): boolean {
  return (ROLE_HIERARCHY[a] || 0) > (ROLE_HIERARCHY[b] || 0);
}
